import { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  CalendarDays,
  Clock,
  User,
  Phone,
  Mail,
  FileText,
  MoreVertical,
  Star,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Inbox,
  Shield,
  Briefcase,
  Plus,
  Trash2,
  Banknote,
  Users,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [appointments, setAppointments] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [messages, setMessages] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftApps, setShiftApps] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isAuthed, setIsAuthed] = useState(false);
  const [pin, setPin] = useState("");
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [shiftForm, setShiftForm] = useState({ title: "", role: "Locum Doctor", date: "", start_time: "", end_time: "", rate: "", description: "", slots_available: 1 });

  const fetchData = async () => {
    try {
      const [apptRes, testRes, msgRes, shiftRes, shiftAppRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/testimonials/all`),
        axios.get(`${API}/contact-messages`),
        axios.get(`${API}/shifts`),
        axios.get(`${API}/shift-applications`),
      ]);
      setAppointments(apptRes.data);
      setTestimonials(testRes.data);
      setMessages(msgRes.data);
      setShifts(shiftRes.data);
      setShiftApps(shiftAppRes.data);
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => {
    if (isAuthed) fetchData();
  }, [isAuthed]);

  const updateAppointmentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/appointments/${id}/status`, { status });
      toast.success(`Appointment ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const approveTestimonial = async (id) => {
    try {
      await axios.put(`${API}/testimonials/${id}/approve`);
      toast.success("Testimonial approved");
      fetchData();
    } catch {
      toast.error("Failed to approve");
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      toast.success("Testimonial deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const createShift = async () => {
    if (!shiftForm.title || !shiftForm.date || !shiftForm.start_time || !shiftForm.end_time) {
      toast.error("Please fill in shift title, date, and times");
      return;
    }
    try {
      await axios.post(`${API}/shifts`, shiftForm);
      toast.success("Shift created");
      setShowShiftForm(false);
      setShiftForm({ title: "", role: "Locum Doctor", date: "", start_time: "", end_time: "", rate: "", description: "", slots_available: 1 });
      fetchData();
    } catch {
      toast.error("Failed to create shift");
    }
  };

  const deleteShift = async (id) => {
    try {
      await axios.delete(`${API}/shifts/${id}`);
      toast.success("Shift deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete shift");
    }
  };

  const toggleShiftStatus = async (shift) => {
    try {
      if (shift.status === "open") {
        await axios.put(`${API}/shifts/${shift.id}/close`);
        toast.success("Shift closed");
      } else {
        await axios.put(`${API}/shifts/${shift.id}/reopen`);
        toast.success("Shift reopened");
      }
      fetchData();
    } catch {
      toast.error("Failed to update shift");
    }
  };

  const updateShiftAppStatus = async (id, status) => {
    try {
      await axios.put(`${API}/shift-applications/${id}/status`, { status });
      toast.success(`Application ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const filteredAppointments =
    statusFilter === "all"
      ? appointments
      : appointments.filter((a) => a.status === statusFilter);

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 pt-20">
        <div className="max-w-sm w-full bg-white rounded-3xl p-8 border border-stone-100 shadow-sm text-center" data-testid="admin-login">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-green-700" />
          </div>
          <h2 className="font-[Outfit] text-xl font-bold text-stone-900 mb-2">Admin Access</h2>
          <p className="text-stone-500 text-sm mb-6">Enter the admin PIN to continue</p>
          <input
            data-testid="admin-pin-input"
            type="password"
            placeholder="Enter PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (pin === "Taale123") setIsAuthed(true);
                else toast.error("Invalid PIN");
              }
            }}
            className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-center text-lg tracking-widest mb-4 focus:outline-none focus:ring-2 focus:ring-green-600"
          />
          <Button
            data-testid="admin-login-btn"
            onClick={() => {
              if (pin === "Taale123") setIsAuthed(true);
              else toast.error("Invalid PIN");
            }}
            className="w-full bg-green-700 hover:bg-green-800 text-white rounded-full py-3"
          >
            Enter Dashboard
          </Button>
          <p className="text-xs text-stone-400 mt-4">Contact admin for PIN</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16 px-4" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-[Outfit] text-3xl font-bold text-stone-900 tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-stone-500 mt-1">Manage appointments, testimonials and messages</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: "Total Appointments", value: appointments.length, color: "bg-green-100 text-green-800" },
            { label: "Pending", value: appointments.filter((a) => a.status === "pending").length, color: "bg-amber-100 text-amber-800" },
            { label: "Testimonials", value: testimonials.length, color: "bg-blue-100 text-blue-800" },
            { label: "Messages", value: messages.length, color: "bg-purple-100 text-purple-800" },
            { label: "Open Shifts", value: shifts.filter((s) => s.status === "open").length, color: "bg-orange-100 text-orange-800" },
            { label: "Shift Applications", value: shiftApps.length, color: "bg-cyan-100 text-cyan-800" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-stone-100 p-5">
              <p className="text-stone-500 text-xs font-medium">{stat.label}</p>
              <p className="text-2xl font-bold text-stone-900 mt-1">{stat.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="appointments" className="w-full">
          <TabsList className="bg-white border border-stone-100 rounded-xl p-1 mb-6">
            <TabsTrigger value="appointments" className="rounded-lg data-[state=active]:bg-green-700 data-[state=active]:text-white" data-testid="tab-appointments">
              <CalendarDays className="w-4 h-4 mr-2" /> Appointments
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-lg data-[state=active]:bg-green-700 data-[state=active]:text-white" data-testid="tab-testimonials">
              <Star className="w-4 h-4 mr-2" /> Testimonials
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-lg data-[state=active]:bg-green-700 data-[state=active]:text-white" data-testid="tab-messages">
              <MessageSquare className="w-4 h-4 mr-2" /> Messages
            </TabsTrigger>
            <TabsTrigger value="shifts" className="rounded-lg data-[state=active]:bg-green-700 data-[state=active]:text-white" data-testid="tab-shifts">
              <Briefcase className="w-4 h-4 mr-2" /> Shifts
            </TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm text-stone-500">Filter:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 rounded-xl border-stone-200 bg-white" data-testid="appointment-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filteredAppointments.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
                <Inbox className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400">No appointments found</p>
              </div>
            )}

            <div className="space-y-3">
              {filteredAppointments.map((appt) => (
                <div key={appt.id} className="bg-white rounded-2xl border border-stone-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`appointment-${appt.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-semibold text-stone-900 truncate">{appt.patient_name}</p>
                      <Badge
                        className={`text-xs rounded-full ${
                          appt.status === "pending"
                            ? "bg-amber-100 text-amber-800 border-0"
                            : appt.status === "confirmed"
                            ? "bg-green-100 text-green-800 border-0"
                            : appt.status === "completed"
                            ? "bg-blue-100 text-blue-800 border-0"
                            : "bg-red-100 text-red-800 border-0"
                        }`}
                      >
                        {appt.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                      <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{appt.service}</span>
                      <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{appt.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{appt.time_slot}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{appt.phone}</span>
                      {appt.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{appt.email}</span>}
                    </div>
                    {appt.notes && <p className="text-xs text-stone-400 mt-1 italic">"{appt.notes}"</p>}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-xl border-stone-200" data-testid={`appointment-actions-${appt.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-xl">
                      <DropdownMenuItem onClick={() => updateAppointmentStatus(appt.id, "confirmed")}>
                        <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" /> Confirm
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateAppointmentStatus(appt.id, "completed")}>
                        <CheckCircle2 className="w-4 h-4 mr-2 text-blue-600" /> Complete
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateAppointmentStatus(appt.id, "cancelled")}>
                        <XCircle className="w-4 h-4 mr-2 text-red-600" /> Cancel
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            {testimonials.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
                <Star className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400">No testimonials yet</p>
              </div>
            )}
            <div className="space-y-3">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white rounded-2xl border border-stone-100 p-5 flex flex-col sm:flex-row sm:items-start gap-4" data-testid={`testimonial-${t.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-semibold text-stone-900">{t.patient_name}</p>
                      <div className="flex gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <Badge className={`text-xs rounded-full ${t.is_approved ? "bg-green-100 text-green-800 border-0" : "bg-amber-100 text-amber-800 border-0"}`}>
                        {t.is_approved ? "Approved" : "Pending"}
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-600 italic">"{t.message}"</p>
                  </div>
                  <div className="flex gap-2">
                    {!t.is_approved && (
                      <Button
                        size="sm"
                        onClick={() => approveTestimonial(t.id)}
                        className="bg-green-700 hover:bg-green-800 text-white rounded-xl text-xs"
                        data-testid={`approve-testimonial-${t.id}`}
                      >
                        Approve
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteTestimonial(t.id)}
                      className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`delete-testimonial-${t.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            {messages.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
                <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400">No messages yet</p>
              </div>
            )}
            <div className="space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className="bg-white rounded-2xl border border-stone-100 p-5" data-testid={`message-${msg.id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <p className="font-semibold text-stone-900">{msg.name}</p>
                    <span className="text-xs text-stone-400">{new Date(msg.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-stone-600 mb-2">{msg.message}</p>
                  <div className="flex gap-4 text-xs text-stone-500">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{msg.email}</span>
                    {msg.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{msg.phone}</span>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Shifts Tab */}
          <TabsContent value="shifts">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-[Outfit] text-lg font-semibold text-stone-900">Manage Shifts</h3>
              <Button data-testid="add-shift-btn" onClick={() => setShowShiftForm(!showShiftForm)} className="bg-green-700 hover:bg-green-800 text-white rounded-full px-5 gap-2">
                <Plus className="w-4 h-4" /> Add Shift
              </Button>
            </div>

            {/* Add Shift Form */}
            {showShiftForm && (
              <div className="bg-stone-50 rounded-2xl border border-stone-200 p-6 mb-6" data-testid="add-shift-form">
                <h4 className="font-semibold text-stone-900 mb-4">New Shift</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Shift Title *</Label>
                    <Input data-testid="shift-title-input" placeholder="e.g. Morning Ward Cover" value={shiftForm.title} onChange={(e) => setShiftForm({...shiftForm, title: e.target.value})} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Role *</Label>
                    <Select value={shiftForm.role} onValueChange={(v) => setShiftForm({...shiftForm, role: v})}>
                      <SelectTrigger className="bg-white rounded-xl border-stone-200" data-testid="shift-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Locum Doctor">Locum Doctor</SelectItem>
                        <SelectItem value="Nurse">Nurse</SelectItem>
                        <SelectItem value="Lab Scientist">Lab Scientist</SelectItem>
                        <SelectItem value="Lab Technician">Lab Technician</SelectItem>
                        <SelectItem value="Receptionist">Receptionist</SelectItem>
                        <SelectItem value="Admin Staff">Admin Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Date *</Label>
                    <Input data-testid="shift-date-input" type="date" value={shiftForm.date} onChange={(e) => setShiftForm({...shiftForm, date: e.target.value})} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Start Time *</Label>
                    <Input data-testid="shift-start-input" type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">End Time *</Label>
                    <Input data-testid="shift-end-input" type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Rate (Optional)</Label>
                    <Input data-testid="shift-rate-input" placeholder="e.g. ₦25,000/shift" value={shiftForm.rate} onChange={(e) => setShiftForm({...shiftForm, rate: e.target.value})} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Slots Available</Label>
                    <Input data-testid="shift-slots-input" type="number" min="1" value={shiftForm.slots_available} onChange={(e) => setShiftForm({...shiftForm, slots_available: parseInt(e.target.value) || 1})} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-sm text-stone-600 mb-1 block">Description (Optional)</Label>
                    <Input data-testid="shift-desc-input" placeholder="Brief description of the shift duties..." value={shiftForm.description} onChange={(e) => setShiftForm({...shiftForm, description: e.target.value})} className="bg-white rounded-xl border-stone-200" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button data-testid="save-shift-btn" onClick={createShift} className="bg-green-700 hover:bg-green-800 text-white rounded-full px-6">Save Shift</Button>
                  <Button variant="outline" onClick={() => setShowShiftForm(false)} className="rounded-full px-6 border-stone-200">Cancel</Button>
                </div>
              </div>
            )}

            {/* Shifts List */}
            {shifts.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
                <Briefcase className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400">No shifts created yet. Click "Add Shift" to get started.</p>
              </div>
            )}

            <div className="space-y-3">
              {shifts.map((shift) => (
                <div key={shift.id} className="bg-white rounded-2xl border border-stone-100 p-5" data-testid={`admin-shift-${shift.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <p className="font-semibold text-stone-900">{shift.role}</p>
                        <Badge className={`text-xs rounded-full border-0 ${shift.status === "open" ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-600"}`}>
                          {shift.status}
                        </Badge>
                        <Badge className="text-xs rounded-full border-0 bg-blue-100 text-blue-800">
                          {shift.slots_filled}/{shift.slots_available} filled
                        </Badge>
                      </div>
                      <p className="text-sm text-stone-700 font-medium mb-1">{shift.title}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                        <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{shift.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{shift.start_time} - {shift.end_time}</span>
                        {shift.rate && <span className="flex items-center gap-1"><Banknote className="w-3 h-3" />{shift.rate}</span>}
                      </div>
                      {/* Show applications for this shift */}
                      {shiftApps.filter(a => a.shift_id === shift.id).length > 0 && (
                        <div className="mt-3 border-t border-stone-100 pt-3">
                          <p className="text-xs font-semibold text-stone-500 mb-2">APPLICATIONS ({shiftApps.filter(a => a.shift_id === shift.id).length})</p>
                          <div className="space-y-2">
                            {shiftApps.filter(a => a.shift_id === shift.id).map(app => (
                              <div key={app.id} className="bg-stone-50 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="text-sm font-medium text-stone-900">{app.applicant_name}</p>
                                    <Badge className={`text-[10px] rounded-full border-0 ${app.status === "pending" ? "bg-amber-100 text-amber-800" : app.status === "accepted" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{app.status}</Badge>
                                  </div>
                                  <div className="flex flex-wrap gap-3 text-xs text-stone-500">
                                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{app.phone}</span>
                                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{app.email}</span>
                                    {app.registration && <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{app.registration}</span>}
                                    {app.cv_url && <a href={`${API.replace('/api', '')}${app.cv_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-green-700 underline"><FileText className="w-3 h-3" />View CV</a>}
                                  </div>
                                  {app.experience && <p className="text-xs text-stone-500 mt-1 italic">"{app.experience}"</p>}
                                </div>
                                <div className="flex gap-2">
                                  {app.status === "pending" && (
                                    <>
                                      <Button size="sm" onClick={() => updateShiftAppStatus(app.id, "accepted")} className="bg-green-700 hover:bg-green-800 text-white rounded-xl text-xs">Accept</Button>
                                      <Button size="sm" variant="outline" onClick={() => updateShiftAppStatus(app.id, "rejected")} className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50">Reject</Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => toggleShiftStatus(shift)} className="rounded-xl text-xs" data-testid={`toggle-shift-${shift.id}`}>
                        {shift.status === "open" ? "Close" : "Reopen"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteShift(shift.id)} className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-shift-${shift.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
